<template>
  <div class="avatar-container" @click="handleClick">
    <!-- 头像框 -->
    <div
      v-if="shouldShowFrame"
      :class="`avatar-frame avatar-frame-${size}`"
      :style="{ backgroundImage: `url(${avatarFrame})` }"
    ></div>
    <div
      class="avatar-wrapper"
      :class="[
        `avatar-${size}`,
        {
          clickable: clickable,
          'official-account': isOfficialAccount,
          'has-frame': shouldShowFrame,
        },
      ]"
    >
      <img
        :src="avatarSrc"
        :alt="alt"
        class="avatar-image"
        @error="handleImageError"
      />
      <!-- 官方账号特殊背景 -->
      <div v-if="isOfficialAccount" class="official-background"></div>
    </div>
    <!-- 官方账号标志 -->
    <img
      v-if="isOfficialAccount"
      :src="officialIconSrc"
      :alt="'official'"
      class="official-icon"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useUserStore, GAME_ID, useLanguageStore } from "@/stores";
import { OFFICIAL_ACCOUNT_LIST } from "@/stores/user";
const userStore = useUserStore();
const languageStore = useLanguageStore();
interface Props {
  avatarIndex?: number | string;
  size?: "small" | "medium" | "large";
  clickable?: boolean;
  isSystemAvatar?: boolean;
  alt?: string;
  // 新增：用户ID，用于自动导航
  userId?: number | string;
  // 新增：是否启用自动导航到用户页面
  enableNavigate?: boolean;
  avatarFrame?: string;
}
const DEFAULT_AVATAR_INDEX = 1;
const props = withDefaults(defineProps<Props>(), {
  avatarIndex: DEFAULT_AVATAR_INDEX,
  size: "medium",
  clickable: false,
  isSystemAvatar: false,
  alt: "avatar",
  userId: "",
  enableNavigate: false,
  avatarFrame: "",
});
const emit = defineEmits<{
  (e: "click"): void;
}>();

const router = useRouter();
const route = useRoute();

const avatarSrc = computed(() => {
  // 特殊头像处理，由前端写死，二期会改造成后端接口获取
  if (props.userId == "10000001") {
    return new URL(`../../assets/avatar/191/special1.jpg`, import.meta.url)
      .href;
  }
  if (props.userId == "10053512") {
    return new URL(`../../assets/avatar/430/special1.png`, import.meta.url)
      .href;
  }
  if (props.userId == "10053514") {
    return new URL(`../../assets/avatar/430/special2.png`, import.meta.url)
      .href;
  }
  if (props.userId == "10124208") {
    return new URL(`../../assets/avatar/386/special1.png`, import.meta.url)
      .href;
  }
  if (props.userId == "10124210") {
    return new URL(`../../assets/avatar/386/special2.png`, import.meta.url)
      .href;
  }
  if (props.userId == "10124211") {
    return new URL(`../../assets/avatar/386/special3.png`, import.meta.url)
      .href;
  }
  if (props.userId == "10124586") {
    return new URL(`../../assets/avatar/386/special4.png`, import.meta.url)
      .href;
  }
  if (props.isSystemAvatar) {
    return new URL(`../../assets/announcement.png`, import.meta.url).href;
  }
  const index = +props.avatarIndex || DEFAULT_AVATAR_INDEX;
  // 先尝试从 language store 获取头像
  const imageKey = `face${index}`;
  const imageFromStore = languageStore.getImage(imageKey);
  if (imageFromStore) {
    return imageFromStore;
  }
  // 如果获取不到，则使用现有逻辑
  return new URL(
    `../../assets/avatar/${GAME_ID}/face${index}.jpg`,
    import.meta.url
  ).href;
});

// 官方账号图标路径
const officialIconSrc = computed(() => {
  return new URL(`../../assets/icon-official.png`, import.meta.url).href;
});

// 检查是否为官方账号
const isOfficialAccount = computed(() => {
  return props.userId && OFFICIAL_ACCOUNT_LIST.includes(+props.userId);
});

// 判断是否应该显示头像框（有头像框且不是官方账号）
const shouldShowFrame = computed(() => {
  return !!props.avatarFrame;
});

const handleClick = (event: Event) => {
  // 阻止冒泡
  if (props.clickable) {
    event.stopPropagation();
    // 如果启用了自动导航且有用户ID，则自动导航到用户页面
    if (props.enableNavigate && props.userId) {
      navigateToUser(props.userId as string);
    } else {
      // 否则触发click事件，保持向后兼容
      emit("click");
    }
  }
};

const navigateToUser = (uid: string) => {
  if (!uid) return;

  router.push({
    path: `/user/${uid}`,
    query: {
      ...route.query,
    },
  });
};

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement;
  // 如果头像加载失败，使用默认头像
  target.src = new URL(
    `../../assets/avatar/${GAME_ID}/face1.jpg`,
    import.meta.url
  ).href;
};
</script>

<style scoped lang="scss">
.avatar-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.avatar-wrapper {
  display: inline-block;
  border-radius: 50%;
  overflow: hidden;
  position: relative;

  &.clickable {
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }
  }

  // 官方账号特殊样式
  &.official-account {
    border: 3px solid #ffd700;
    position: relative;
  }

  // 有头像框时不需要 border
  &.has-frame {
    border: none;
  }
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

// 头像框样式
.avatar-frame {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 115%;
  height: 115%;
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  pointer-events: none;
  z-index: 1;
}
// 官方账号标志样式
.official-icon {
  position: absolute;
  bottom: 0;
  right: -2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  padding: 1px;
  z-index: 1;
}

// 尺寸变体
.avatar-small {
  width: 32px;
  height: 32px;
  border-width: 2px !important;
}

.avatar-medium {
  width: 44px;
  height: 44px;
}

.avatar-large {
  width: 60px;
  height: 60px;
  border-width: 3px !important;

  ~ .official-icon {
    width: 25px;
    height: 25px;
  }
}
</style>
